import React from "react";
import { Link } from "react-router-dom";
import { Mail, Phone, Linkedin, ArrowUpRight, MapPin } from "lucide-react";
import AmbientParticles from "./AmbientParticles";

/* ==================================================================
   Footer - designed to feel like an established practice, not a
   template. Structured columns, regulatory authority strip, brand
   signature, no fake trust badges.
   ================================================================== */

const EXPLORE = {
  primary: [
    {
      to: "/growth-partnership",
      label: "Growth Partnership",
      testid: "footer-link-partnership",
    },
  ],
  broader: [
    { to: "/diagnose", label: "Diagnose", testid: "footer-link-diagnose" },
    {
      to: "/the-difference",
      label: "The Difference",
      testid: "footer-link-difference",
    },
  ],
  voice: [
    { to: "/story", label: "The Book", testid: "footer-link-book" },
    { to: "/field-notes", label: "Field Notes", testid: "footer-link-field-notes" },
  ],
};

const REACH = {
  email: "hello@getvirtualgrowth.com",
  phone: "+1 530 444 4477",
  linkedin: "https://www.linkedin.com/in/fieldnotesbykrishna",
};

const REGULATIONS = [
  "CAN-SPAM (US)",
  "UK GDPR + PECR",
  "Australia Spam Act",
  "New Zealand UEM Act",
  "GCC Data Protection Frameworks",
];

const MARKETS = ["US", "UK", "AU", "NZ", "GCC", "and beyond"];

export default function Footer() {
  return (
    <footer
      className="relative bg-bg text-ink overflow-hidden"
      data-testid="site-footer"
    >
      {/* Champagne hairline - footer emerges */}
      <div
        aria-hidden
        className="absolute inset-x-0 top-0 h-px pointer-events-none"
        style={{
          background:
            "linear-gradient(90deg, transparent, rgba(201,169,97,0.35), transparent)",
        }}
      />

      {/* Ambient glow */}
      <div
        aria-hidden
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            "radial-gradient(ellipse 60% 50% at 50% 20%, rgba(201,169,97,0.06), transparent 70%)",
        }}
      />

      <AmbientParticles />

      <div className="relative max-w-7xl mx-auto px-6 md:px-10 lg:px-16 pt-24 md:pt-28 pb-10">
        {/* =========================================================
            BLOCK 1 - Big brand statement + primary CTA
            ========================================================= */}
        <div
          className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-start"
          data-testid="footer-statement"
        >
          <div className="lg:col-span-8">
            <p className="font-sans text-[10px] uppercase tracking-[0.4em] text-brass mb-6">
              Get Virtual Growth
            </p>
            <p
              className="font-serif text-ink leading-[1.05]"
              style={{
                fontSize: "clamp(2.2rem, 5vw, 4rem)",
                letterSpacing: "-0.01em",
              }}
            >
              You keep building your practice.
              <span className="text-brass italic">
                {" "}
                We keep your calendar full.
              </span>
            </p>
            <p className="mt-6 font-sans text-sm md:text-base text-ink-muted leading-relaxed max-w-lg">
              A managed customer acquisition practice for founder-led firms.
              Personalised video outreach, sourced, sent, and booked by our
              team.
            </p>
          </div>

          <div className="lg:col-span-4 flex flex-col items-start lg:items-end">
            <Link
              to="/growth-partnership"
              data-testid="footer-primary-cta"
              className="group inline-flex items-center gap-3 rounded-full border border-brass/40 hover:border-brass px-5 py-3 font-sans text-[11px] uppercase tracking-[0.24em] text-ink hover:text-brass transition-colors duration-500"
            >
              Start with a Pilot
              <ArrowUpRight className="h-3.5 w-3.5 group-hover:-translate-y-0.5 group-hover:translate-x-0.5 transition-transform" />
            </Link>
            <p className="mt-4 font-sans text-xs text-ink-muted lg:text-right max-w-xs leading-relaxed">
              48-hour start. Dedicated team assigned the moment you decide.
            </p>
          </div>
        </div>

        {/* Hairline divider */}
        <div
          className="mt-16 md:mt-20 h-px"
          style={{
            background:
              "linear-gradient(90deg, transparent, rgba(201,169,97,0.28), transparent)",
          }}
        />

        {/* =========================================================
            BLOCK 2 - Structured 4-column grid
            ========================================================= */}
        <div
          className="mt-14 grid grid-cols-2 md:grid-cols-4 gap-10 md:gap-8"
          data-testid="footer-columns"
        >
          {/* Practice - focused service + broader brand-building work */}
          <div data-testid="footer-col-practice">
            <p className="font-sans text-[10px] uppercase tracking-[0.28em] text-brass mb-5">
              Practice
            </p>
            <ul className="space-y-3 font-sans text-sm text-ink">
              {EXPLORE.primary.map((l) => (
                <li key={l.to}>
                  <Link
                    to={l.to}
                    data-testid={l.testid}
                    className="hover:text-brass transition-colors duration-500"
                    style={{ transitionTimingFunction: "cubic-bezier(0.2,0.8,0.2,1)" }}
                  >
                    {l.label}
                  </Link>
                </li>
              ))}
            </ul>

            <p className="mt-8 font-sans text-[9px] uppercase tracking-[0.28em] text-ink-muted mb-3">
              More services
            </p>
            <ul className="space-y-3 font-sans text-sm text-ink-muted">
              {EXPLORE.broader.map((l) => (
                <li key={l.to}>
                  <Link
                    to={l.to}
                    data-testid={l.testid}
                    className="hover:text-brass transition-colors duration-500"
                    style={{ transitionTimingFunction: "cubic-bezier(0.2,0.8,0.2,1)" }}
                  >
                    {l.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Voice */}
          <div data-testid="footer-col-voice">
            <p className="font-sans text-[10px] uppercase tracking-[0.28em] text-brass mb-5">
              Voice
            </p>
            <ul className="space-y-3 font-sans text-sm text-ink">
              {EXPLORE.voice.map((l) => (
                <li key={l.to}>
                  <Link
                    to={l.to}
                    data-testid={l.testid}
                    className="hover:text-brass transition-colors duration-500"
                    style={{ transitionTimingFunction: "cubic-bezier(0.2,0.8,0.2,1)" }}
                  >
                    {l.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Reach */}
          <div data-testid="footer-col-reach">
            <p className="font-sans text-[10px] uppercase tracking-[0.28em] text-brass mb-5">
              Reach
            </p>
            <ul className="space-y-3 font-sans text-sm text-ink">
              <li className="flex items-start gap-3">
                <Mail className="h-4 w-4 mt-0.5 text-brass shrink-0" />
                <a
                  href={`mailto:${REACH.email}`}
                  data-testid="footer-email"
                  className="hover:text-brass transition-colors duration-500 break-all"
                >
                  {REACH.email}
                </a>
              </li>
              <li className="flex items-start gap-3">
                <Phone className="h-4 w-4 mt-0.5 text-brass shrink-0" />
                <a
                  href={`tel:${REACH.phone.replace(/[^\d+]/g, "")}`}
                  data-testid="footer-phone"
                  className="hover:text-brass transition-colors duration-500"
                >
                  {REACH.phone}
                </a>
              </li>
              <li className="flex items-start gap-3">
                <Linkedin className="h-4 w-4 mt-0.5 text-brass shrink-0" />
                <a
                  href={REACH.linkedin}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="footer-linkedin"
                  className="hover:text-brass transition-colors duration-500 inline-flex items-center gap-1"
                >
                  Founder on LinkedIn
                  <ArrowUpRight className="h-3 w-3" />
                </a>
              </li>
            </ul>
          </div>

          {/* Registered office */}
          <div data-testid="footer-col-office">
            <p className="font-sans text-[10px] uppercase tracking-[0.28em] text-brass mb-5">
              Registered
            </p>
            <div className="flex items-start gap-3 font-sans text-sm text-ink leading-relaxed">
              <MapPin className="h-4 w-4 mt-0.5 text-brass shrink-0" />
              <address className="not-italic">
                Get Virtual Growth
                <br />
                Meydan Grandstand Level 6
                <br />
                Meydan Road, Nad Al Sheba
                <br />
                Dubai, UAE
              </address>
            </div>
          </div>
        </div>

        {/* Hairline divider */}
        <div
          className="mt-16 md:mt-20 h-px"
          style={{
            background:
              "linear-gradient(90deg, transparent, rgba(201,169,97,0.20), transparent)",
          }}
        />

        {/* =========================================================
            BLOCK 3 - Authority strip: markets + regulations
            ========================================================= */}
        <div
          className="mt-14 grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-8"
          data-testid="footer-authority"
        >
          <div className="md:col-span-4">
            <p className="font-sans text-[10px] uppercase tracking-[0.28em] text-brass mb-5">
              Markets served
            </p>
            <div className="flex flex-wrap gap-2">
              {MARKETS.map((m) => (
                <span
                  key={m}
                  className="inline-flex items-center rounded-full border border-brass/25 px-3 py-1 font-mono text-[10px] uppercase tracking-widest text-ink"
                >
                  {m}
                </span>
              ))}
            </div>
          </div>

          <div className="md:col-span-8">
            <p className="font-sans text-[10px] uppercase tracking-[0.28em] text-brass mb-5">
              Operates in compliance with
            </p>
            <p className="font-sans text-sm text-ink-muted leading-relaxed">
              {REGULATIONS.map((r, i) => (
                <React.Fragment key={r}>
                  <span className="text-ink">{r}</span>
                  {i < REGULATIONS.length - 1 && (
                    <span className="text-brass/50 mx-2">·</span>
                  )}
                </React.Fragment>
              ))}
              .
            </p>
            <p className="mt-4 font-sans text-xs text-ink-muted italic">
              Messaging stays professional. Opt-outs honoured the first time
              asked. Nothing that touches financial, legal or medical advice
              ships without review.
            </p>
          </div>
        </div>

        {/* Hairline divider */}
        <div
          className="mt-16 h-px"
          style={{
            background:
              "linear-gradient(90deg, transparent, rgba(201,169,97,0.20), transparent)",
          }}
        />

        {/* =========================================================
            BLOCK 4 - Brand signature (walk the talk)
            ========================================================= */}
        <div className="mt-14 text-center" data-testid="footer-signature">
          <p
            className="font-serif italic text-brass leading-snug mx-auto"
            style={{ fontSize: "clamp(1.05rem, 1.8vw, 1.4rem)" }}
          >
            &ldquo;Vasudhaiva Kutumbakam.&rdquo;
          </p>
          <p className="mt-2 font-sans text-xs uppercase tracking-[0.32em] text-ink-muted">
            The world is one family
          </p>
        </div>

        {/* =========================================================
            BLOCK 5 - Legal row
            ========================================================= */}
        <div
          className="mt-14 pt-8 border-t border-brass/12 flex flex-col md:flex-row items-start md:items-center justify-between gap-4 md:gap-6"
          data-testid="footer-legal"
        >
          <p className="font-sans text-xs text-ink-muted">
            &copy; {new Date().getFullYear()} Get Virtual Growth. All rights
            reserved.
          </p>
          <ul className="flex flex-wrap items-center gap-x-6 gap-y-2 font-sans text-xs text-ink-muted">
            <li>
              <Link
                to="/privacy"
                data-testid="footer-privacy"
                className="hover:text-brass transition-colors duration-500"
              >
                Privacy
              </Link>
            </li>
            <li>
              <Link
                to="/terms"
                data-testid="footer-terms"
                className="hover:text-brass transition-colors duration-500"
              >
                Terms
              </Link>
            </li>
            <li>
              <a
                href="/sitemap.xml"
                data-testid="footer-sitemap"
                className="hover:text-brass transition-colors duration-500"
              >
                Sitemap
              </a>
            </li>
          </ul>
        </div>
      </div>
    </footer>
  );
}
